/**
 * DailyCheckInForm Component
 *
 * Date-bound daily check-in form for athletes.  Supports two access modes:
 *
 * 1. **Token mode** (SMS link): `/daily-checkin/2026-03-04?token=...`
 *    Uses public token-validated endpoints — no login required.
 *
 * 2. **Auth mode** (hamburger menu → "Daily Check-In"):
 *    `/daily-checkin/today` resolves to today's date and uses the JWT
 *    from `localStorage` to call authenticated endpoints.
 *
 * The component reuses the same MetricSlider UI as AthleteCheckIn.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { MetricSlider } from '@/components/shared/MetricSlider';
import { BodyRegionPicker } from '@/components/shared/BodyRegionPicker';
import { Header } from '@/components/shared';
import { useTheme } from '@/contexts/ThemeContext';
import { getApiUrl } from '@/config/api';
import { Dumbbell, Flame, Brain, Activity, Calendar, AlertTriangle, CheckCircle2, Lightbulb } from '@/utils/icons';
import type { RegionalPainEntry } from '@/types/sessions';

/** Shape returned by both the token and auth GET endpoints. */
interface DailyCheckInResponse {
  check_in_date: string;
  athlete_id: string;
  team_id: number | null;
  existing_checkin: {
    soreness: number;
    motivation: number;
    mental_fatigue: number;
    session_rpe: number;
    notes: string;
    attended: boolean;
  } | null;
}

/** Payload sent to the submit endpoint. */
interface DailyCheckInPayload {
  soreness: number;
  motivation: number;
  mental_fatigue: number;
  session_rpe: number;
  notes: string;
  attended: boolean;
  regional_pain?: RegionalPainEntry[];
}

/** Minimal user info shape for the Header component. */
interface UserInfo {
  username: string;
  role?: string;
  full_name?: string;
  first_name?: string;
}

export const DailyCheckInForm: React.FC = () => {
  const { date: dateParam } = useParams<{ date: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const token = searchParams.get('token');

  /**
   * Determine access mode: if a `token` query param is present we use the
   * public token-validated endpoints (SMS flow).  Otherwise we require a
   * JWT in localStorage and hit the authenticated `/me/` endpoints.
   */
  const jwt = localStorage.getItem('access');
  const isAuthMode = !token && !!jwt;

  /**
   * Resolve the special "today" slug to the current local date string.
   * All other values are passed through as-is.
   *
   * IMPORTANT: Use local date (getFullYear/getMonth/getDate), NOT
   * toISOString() which returns UTC. In US Pacific after 5pm,
   * toISOString() produces tomorrow's date, causing check-ins to
   * overwrite the wrong day.
   */
  const resolvedDate =
    dateParam === 'today'
      ? (() => {
          const d = new Date();
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        })()
      : dateParam ?? '';

  // User info for the Header (auth mode only)
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  // Page state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state — defaults to middle-of-scale
  const [soreness, setSoreness] = useState(5);
  const [motivation, setMotivation] = useState(5);
  const [mentalFatigue, setMentalFatigue] = useState(5);
  const [sessionRpe, setSessionRpe] = useState(5);
  const [notes, setNotes] = useState('');
  const [attended, setAttended] = useState(true);
  const [regionalPain, setRegionalPain] = useState<RegionalPainEntry[]>([]);

  // ---------------------------------------------------------------------------
  // Load user info for auth-mode Header
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isAuthMode) return;
    const stored = localStorage.getItem('user_info');
    if (stored) {
      try {
        setUserInfo(JSON.parse(stored));
      } catch {
        /* ignore parse errors */
      }
    }
  }, [isAuthMode]);

  // ---------------------------------------------------------------------------
  // Redirect to login when neither token nor JWT is available
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!token && !jwt) {
      sessionStorage.setItem('redirectAfterLogin', `/daily-checkin/${dateParam ?? 'today'}`);
      navigate('/login');
    }
  }, [token, jwt, navigate, dateParam]);

  // ---------------------------------------------------------------------------
  // Fetch existing check-in on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!token && !jwt) return; // redirect handled above

    const fetchCheckIn = async () => {
      try {
        const url = isAuthMode
          ? getApiUrl(`/api/teams/daily-checkin/me/${resolvedDate}/`)
          : getApiUrl(`/api/teams/session-token/daily-checkin/${resolvedDate}/?token=${token}`);

        const headers: Record<string, string> = {};
        if (isAuthMode) {
          headers['Authorization'] = `Bearer ${jwt}`;
          headers['Content-Type'] = 'application/json';
        }

        const res = await fetch(url, { headers });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          setError(body.error || 'Invalid or expired link');
          setLoading(false);
          return;
        }

        const data: DailyCheckInResponse = await res.json();

        if (data.existing_checkin) {
          setSoreness(data.existing_checkin.soreness);
          setMotivation(data.existing_checkin.motivation);
          setMentalFatigue(data.existing_checkin.mental_fatigue);
          setSessionRpe(data.existing_checkin.session_rpe);
          setNotes(data.existing_checkin.notes || '');
          setAttended(data.existing_checkin.attended);
        }
      } catch (err) {
        console.error('Error loading daily check-in:', err);
        setError('Error loading check-in. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCheckIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDate, token, jwt, isAuthMode]);

  // ---------------------------------------------------------------------------
  // Submit handler
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const payload: DailyCheckInPayload = {
      soreness,
      motivation,
      mental_fatigue: mentalFatigue,
      session_rpe: sessionRpe,
      notes,
      attended,
      ...(regionalPain.length > 0 ? { regional_pain: regionalPain } : {}),
    };

    try {
      const url = isAuthMode
        ? getApiUrl(`/api/teams/daily-checkin/me/${resolvedDate}/submit/`)
        : getApiUrl(`/api/teams/session-token/daily-checkin/${resolvedDate}/submit/?token=${token}`);

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (isAuthMode) {
        headers['Authorization'] = `Bearer ${jwt}`;
      }

      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Error submitting check-in. Please try again.');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting daily check-in:', err);
      setError('Network error. Please check your connection and try again.');
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render — loading
  // ---------------------------------------------------------------------------
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center cgm-bg-primary">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--spinner-color)] mx-auto" />
          <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>
            Loading check-in...
          </p>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — error (no token, expired, etc.)
  // ---------------------------------------------------------------------------
  if (error && !submitted) {
    return (
      <div className="min-h-screen cgm-bg-primary">
        {isAuthMode && <Header userInfo={userInfo} />}
        <div className="flex items-center justify-center px-4" style={{ minHeight: isAuthMode ? 'calc(100vh - 60px)' : '100vh' }}>
          <div className="max-w-md w-full cgm-card p-6 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Unable to Load Check-In
            </h1>
            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>{error}</p>
            <button
              onClick={() => navigate(isAuthMode ? '/dashboard' : '/')}
              className="cgm-button px-6 py-2 transition-colors"
            >
              {isAuthMode ? 'Go to Dashboard' : 'Go to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — success
  // ---------------------------------------------------------------------------
  if (submitted) {
    return (
      <div className="min-h-screen cgm-bg-primary">
        {isAuthMode && <Header userInfo={userInfo} />}
        <div className="flex items-center justify-center px-4" style={{ minHeight: isAuthMode ? 'calc(100vh - 60px)' : '100vh' }}>
          <div className="max-w-md w-full cgm-card p-6 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
            <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Check-In Complete!
            </h1>
            <p className="mb-6" style={{ color: 'var(--text-secondary)' }}>
              Thanks for your feedback. Your coach will see this alongside your
              recovery metrics.
            </p>
            {isAuthMode && (
              <button
                onClick={() => navigate('/dashboard')}
                className="cgm-button px-6 py-2 transition-colors"
              >
                Back to Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render — form
  // ---------------------------------------------------------------------------
  const displayDate = new Date(resolvedDate + 'T12:00:00').toLocaleDateString(
    'en-US',
    { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  );

  return (
    <div className="min-h-screen cgm-bg-primary">
      {isAuthMode && <Header userInfo={userInfo} />}
      <div className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Page Title */}
          <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
            How did today's training feel?
          </h1>
          <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
            Quick 30-second check-in to help your coach optimize training
          </p>

          {/* Date Badge */}
          <div className="cgm-card p-4 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {displayDate}
              </p>
            </div>
          </div>

          {/* Check-In Form */}
          <form
            onSubmit={handleSubmit}
            className="cgm-card p-6"
          >
            {/* Attended checkbox */}
            <div className="mb-6 pb-6" style={{ borderBottom: '1px solid var(--card-border)' }}>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!attended}
                  onChange={(e) => setAttended(!e.target.checked)}
                  className="w-5 h-5 text-[var(--accent-ui)] border-gray-300 dark:border-gray-600 rounded focus:ring-[var(--accent-ui-focus)] focus:ring-2"
                />
                <span className="ml-3 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                  I didn't train today
                </span>
              </label>
            </div>

            {attended && (
              <>
                <MetricSlider
                  label="Soreness Level"
                  icon={Dumbbell}
                  min={1}
                  max={10}
                  value={soreness}
                  onChange={setSoreness}
                  minLabel="None"
                  maxLabel="Extreme"
                  helpText="How sore are you feeling right now?"
                />

                <MetricSlider
                  label="Motivation"
                  icon={Flame}
                  min={1}
                  max={10}
                  value={motivation}
                  onChange={setMotivation}
                  minLabel="Very Low"
                  maxLabel="Very High"
                  helpText="How motivated did you feel during the session?"
                  invertColor
                />

                <MetricSlider
                  label="Mental Fatigue"
                  icon={Brain}
                  min={1}
                  max={10}
                  value={mentalFatigue}
                  onChange={setMentalFatigue}
                  minLabel="Fresh"
                  maxLabel="Exhausted"
                  helpText="How mentally tired are you?"
                />

                <MetricSlider
                  label="Session Difficulty (RPE)"
                  icon={Activity}
                  min={1}
                  max={10}
                  value={sessionRpe}
                  onChange={setSessionRpe}
                  minLabel="Very Easy"
                  maxLabel="Maximal"
                  helpText="How hard was this session for you?"
                />

                {/* Regional Pain Picker */}
                <BodyRegionPicker value={regionalPain} onChange={setRegionalPain} />

                {/* Notes */}
                <div className="mb-6">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
                    Notes{' '}
                    <span style={{ color: 'var(--text-muted)' }}>
                      (optional, max 200 characters)
                    </span>
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value.slice(0, 200))}
                    className="w-full px-3 py-2 focus:ring-2 focus:ring-[var(--accent-ui-focus)] focus:border-transparent resize-none"
                    style={{
                      backgroundColor: 'var(--bg-card)',
                      border: '1px solid var(--card-border)',
                      color: 'var(--text-primary)',
                    }}
                    rows={3}
                    placeholder="Any additional thoughts about today..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {notes.length}/200 characters
                    </p>
                    {notes.length > 180 && (
                      <p className="text-xs text-orange-500">
                        {200 - notes.length} remaining
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Error message */}
            {error && (
              <div className="mb-6 p-4" style={{ backgroundColor: 'var(--accent-ui-subtle)', border: '1px solid var(--card-border)' }}>
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full font-semibold py-3 px-6 transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: 'var(--text-primary)', color: 'var(--bg-primary)' }}
            >
              {submitting ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit Check-in'
              )}
            </button>

            <div className="mt-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
              Your feedback helps your coach optimise training and prevent injury
            </div>
          </form>

          {/* Help Card */}
          <div className="mt-6 cgm-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
              <h4 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                Why track daily feedback?
              </h4>
            </div>
            <ul className="text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
              <li>Helps coaches personalise your training intensity</li>
              <li>Identifies early warning signs of overtraining</li>
              <li>Tracks recovery patterns over time</li>
              <li>Data-driven performance optimisation</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
